import React from 'react';
import { MerchantInvoice, MerchantBillingSettings } from '../../../types/gst';

interface Props {
  invoice: MerchantInvoice;
  settings?: MerchantBillingSettings;
}

export const CorporateEleganceTemplate: React.FC<Props> = ({ invoice, settings }) => {
  const sellerName = invoice.sellerTradeName || invoice.sellerLegalName || settings?.companyName || 'Corporate Enterprise';
  const sellerGstin = invoice.sellerGstin || settings?.gstin || '';
  const sellerAddress = invoice.sellerAddress || settings?.address || '';

  // Aggregate HSN Summary for Table
  const hsnMap = new Map<string, { taxable: number; cgst: number; sgst: number; igst: number; totalTax: number }>();
  invoice.lineItems.forEach(item => {
    const code = item.hsnSac || 'OTHER';
    const curr = hsnMap.get(code) || { taxable: 0, cgst: 0, sgst: 0, igst: 0, totalTax: 0 };
    curr.taxable += item.taxableAmount;
    curr.cgst += item.cgstAmount;
    curr.sgst += item.sgstAmount;
    curr.igst += item.igstAmount;
    curr.totalTax += (item.cgstAmount + item.sgstAmount + item.igstAmount);
    hsnMap.set(code, curr);
  });

  return (
    <div className="bg-white text-zinc-900 p-10 font-sans max-w-4xl mx-auto border border-zinc-200 shadow-sm rounded-none text-xs print:p-0 print:border-none print:shadow-none">
      {/* Top Header */}
      <div className="flex justify-between items-start border-b border-zinc-900 pb-6 mb-6">
        <div>
          <h1 className="text-2xl font-light tracking-tight text-zinc-950 uppercase">{sellerName}</h1>
          <p className="text-zinc-500 max-w-md mt-1 leading-relaxed">{sellerAddress}</p>
          <div className="mt-2 text-zinc-700 font-mono text-[11px]">
            {sellerGstin && <span>GSTIN: <strong>{sellerGstin}</strong></span>}
            {settings?.pan && <span className="ml-4">PAN: <strong>{settings.pan}</strong></span>}
          </div>
        </div>

        <div className="text-right">
          <div className="text-xl font-medium tracking-widest text-zinc-400 uppercase">INVOICE</div>
          <div className="font-mono text-sm font-semibold mt-1">#{invoice.invoiceNumber}</div>
          <div className="text-zinc-500 mt-1">Date: {invoice.invoiceDate}</div>
          {invoice.dueDate && <div className="text-zinc-500">Due: {invoice.dueDate}</div>}
        </div>
      </div>

      {/* Addresses */}
      <div className="grid grid-cols-2 gap-8 mb-6 pb-6 border-b border-zinc-200">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">Client Organization</div>
          <div className="font-semibold text-zinc-900 text-sm mt-1">{invoice.customerName}</div>
          <div className="text-zinc-600 mt-0.5 leading-relaxed">{invoice.customerBillingAddress || 'Address on record'}</div>
          {invoice.customerGstin && (
            <div className="font-mono text-zinc-800 mt-1">GSTIN: {invoice.customerGstin}</div>
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">Jurisdiction & Logistics</div>
          <div className="text-zinc-700 mt-1">Place of Supply: <strong>{invoice.placeOfSupply}</strong></div>
          <div className="text-zinc-700">Tax Type: {invoice.isInterState ? 'Inter-State Supply (IGST)' : 'Intra-State Supply (CGST & SGST)'}</div>
          {invoice.eInvoice?.irn && (
            <div className="font-mono text-[10px] text-zinc-500 truncate mt-1">
              IRN: {invoice.eInvoice.irn}
            </div>
          )}
        </div>
      </div>

      {/* Line Items */}
      <table className="w-full text-left mb-6">
        <thead>
          <tr className="border-b border-zinc-900 text-zinc-500 text-[10px] font-semibold uppercase tracking-wider">
            <th className="py-2">Description</th>
            <th className="py-2 text-center">HSN/SAC</th>
            <th className="py-2 text-right">Qty</th>
            <th className="py-2 text-right">Unit Rate</th>
            <th className="py-2 text-right">Taxable</th>
            <th className="py-2 text-right">GST Rate</th>
            <th className="py-2 text-right">Net Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {invoice.lineItems.map((item, idx) => (
            <tr key={idx}>
              <td className="py-3 font-medium text-zinc-900">{item.description}</td>
              <td className="py-3 text-center font-mono text-zinc-500">{item.hsnSac || '—'}</td>
              <td className="py-3 text-right font-mono">{item.quantity}</td>
              <td className="py-3 text-right font-mono">₹{item.unitPrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              <td className="py-3 text-right font-mono">₹{item.taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              <td className="py-3 text-right font-mono text-zinc-600">{item.gstRate}%</td>
              <td className="py-3 text-right font-mono font-medium">₹{item.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Statutory HSN Breakdown Table */}
      <div className="mb-6">
        <div className="text-[10px] uppercase font-bold text-zinc-500 mb-1.5 tracking-wider">Statutory Tax Breakup (HSN/SAC Summary)</div>
        <table className="w-full text-left text-[11px] border border-zinc-200">
          <thead className="bg-zinc-50 text-[10px] text-zinc-600 uppercase border-b border-zinc-200">
            <tr>
              <th className="p-1.5">HSN/SAC</th>
              <th className="p-1.5 text-right">Taxable Val (₹)</th>
              <th className="p-1.5 text-right">CGST (₹)</th>
              <th className="p-1.5 text-right">SGST (₹)</th>
              <th className="p-1.5 text-right">IGST (₹)</th>
              <th className="p-1.5 text-right">Total Tax (₹)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 font-mono">
            {Array.from(hsnMap.entries()).map(([hsn, val], i) => (
              <tr key={i}>
                <td className="p-1.5 font-sans font-medium text-zinc-800">{hsn}</td>
                <td className="p-1.5 text-right">{val.taxable.toFixed(2)}</td>
                <td className="p-1.5 text-right">{val.cgst.toFixed(2)}</td>
                <td className="p-1.5 text-right">{val.sgst.toFixed(2)}</td>
                <td className="p-1.5 text-right">{val.igst.toFixed(2)}</td>
                <td className="p-1.5 text-right font-bold text-zinc-900">{val.totalTax.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bottom Summary */}
      <div className="grid grid-cols-2 gap-8 border-t border-zinc-900 pt-6">
        <div className="space-y-2 text-zinc-600 text-[11px]">
          <div><strong>Bank:</strong> {settings?.bankName || 'HDFC Bank Ltd'}</div>
          <div><strong>A/C Number:</strong> <span className="font-mono">{settings?.accountNumber || '50200088991122'}</span></div>
          <div><strong>IFSC:</strong> <span className="font-mono">{settings?.ifscCode || 'HDFC0000240'}</span></div>
          {settings?.upiId && <div><strong>UPI:</strong> <span className="font-mono">{settings.upiId}</span></div>}
          <div className="text-[10px] text-zinc-500 pt-2 whitespace-pre-line leading-relaxed">
            {invoice.termsAndConditions || settings?.defaultTerms}
          </div>
        </div>

        <div className="space-y-1.5 text-right">
          <div className="flex justify-between text-zinc-600">
            <span>Taxable Total:</span>
            <span className="font-mono">₹{invoice.subtotalTaxable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-zinc-600">
            <span>Total Taxes:</span>
            <span className="font-mono">₹{invoice.totalTax.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between border-t border-zinc-900 pt-2 font-light text-xl text-zinc-950">
            <span>Amount Due:</span>
            <span className="font-mono font-medium">₹{invoice.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="pt-10">
            <div className="font-semibold text-zinc-900">For {sellerName}</div>
            <div className="text-[10px] text-zinc-400 uppercase tracking-widest mt-1">Authorized Signatory</div>
          </div>
        </div>
      </div>
    </div>
  );
};
